import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import coursePages from "../data/coursePagesData";
import { trackCourseDownload, trackWhatsAppClick } from "../utils/analytics";
import "../styles/CoursePage.css";

const whatsappNumber = "5579118589";

function CoursePage() {
  const { courseSlug } = useParams();
  const course = coursePages.find((item) => item.slug === courseSlug);

  if (!course) {
    return (
      <main className="course-page">
        <div className="container">
          <h1>Página no encontrada</h1>
          <p>La ruta que buscaste no existe.</p>
          <Link to="/">Volver al inicio</Link>
        </div>
      </main>
    );
  }

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: course.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const courseJsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: course.title,
    description: course.metaDescription,
    provider: {
      "@type": "Organization",
      name: "Fundación Nican",
      sameAs: "https://fundacionnican.com/",
    },
    areaServed: [
      "Ixtapaluca",
      "Chalco",
      "Valle de Chalco",
      "Estado de México",
    ],
  };

  const message = `Hola, quiero más información sobre ${course.title}.`;

  return (
    <>
      <Helmet>
        <html lang="es-MX" />
        <title>{course.metaTitle}</title>
        <meta name="description" content={course.metaDescription} />
        <link
          rel="canonical"
          href={`https://fundacionnican.com/${course.slug}`}
        />
        <meta property="og:title" content={course.metaTitle} />
        <meta property="og:description" content={course.metaDescription} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://fundacionnican.com/${course.slug}`}
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(courseJsonLd)}
        </script>
      </Helmet>

      <main className="course-page">
        <section className="course-hero">
          <div className="container">
            <Link className="course-back-link" to="/#certificaciones">
              Volver a certificaciones
            </Link>
            <h1>{course.title}</h1>
            <p>{course.lead}</p>
            <a
              className="course-primary-cta"
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                message
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackWhatsAppClick("pagina_curso_hero", {
                  course_name: course.title,
                })
              }
            >
              Solicita información por WhatsApp
            </a>
          </div>
        </section>

        <section className="container course-content">
          <div className="course-grid">
            <div>
              {course.sections.map((section) => (
                <section key={section.heading} className="course-section">
                  <h2>{section.heading}</h2>
                  <p>{section.text}</p>
                </section>
              ))}

              <section className="course-section">
                <h2>Preguntas frecuentes</h2>
                {course.faqs.map((faq) => (
                  <div className="course-faq" key={faq.question}>
                    <h3>{faq.question}</h3>
                    <p>{faq.answer}</p>
                  </div>
                ))}
              </section>
            </div>

            <aside className="course-summary">
              <h2>Beneficios del programa</h2>
              <ul>
                {course.benefits.map((benefit) => (
                  <li key={benefit}>{benefit}</li>
                ))}
              </ul>
              {course.downloadable && (
                <a
                  className="course-download-link"
                  href={require(`../data/Informacion/${course.downloadable}`)}
                  download={course.downloadable}
                  onClick={() =>
                    trackCourseDownload(
                      course.title,
                      course.downloadable,
                      "pagina_curso_resumen"
                    )
                  }
                >
                  Descargar información del curso
                </a>
              )}
              <h2>Búsquedas que atiende</h2>
              <ul>
                {course.keywords.map((keyword) => (
                  <li key={keyword}>{keyword}</li>
                ))}
              </ul>
            </aside>
          </div>
        </section>
      </main>
    </>
  );
}

export default CoursePage;
